<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-card">
      <h5 class="mb-2">{{ $t('competitions.upload_manual_title') || 'Subir video' }}</h5>
      <p class="small text-muted mb-2" v-if="athlete">
        {{ athlete.name }} {{ athlete.surname }}
      </p>
      <p class="small text-muted mb-2" v-if="roundLabel">
        {{ $t('competitions.messages.round_label') || 'Ronda' }}: {{ roundLabel }}
      </p>
      <input
        v-if="progressPercent === null && !isUploading"
        type="file"
        accept="video/mp4,video/webm,video/x-matroska,video/avi,video/*"
        class="form-control mb-2"
        :disabled="isUploading"
        @change="onFileChange"
      />

      <template v-if="progressPercent === null && !isUploading">
        <p class="code-reminder" v-if="code">
          {{ $t('competitions.messages.code_reminder_show') || 'Recuerda mostrar este código en tu video:' }}
          <span class="code-pill">{{ code }}</span>
        </p>
        <p class="code-reminder text-muted" v-else>
          {{ $t('competitions.messages.code_reminder_missing') || 'Recuerda incluir tu código de verificación al grabar el video para que el juez pueda validarlo.' }}
        </p>
      </template>

      <div v-if="progressPercent !== null" class="progress-wrapper">
        <div
          class="progress-medallion"
          :class="{ 'progress-medallion--done': isCompleted }"
          :style="progressMedallionStyle"
        >
          <div class="progress-medallion__value">
            <div class="fw-bold" style="font-size:1.6rem;">{{ displayPercent }}%</div>
            <small class="text-muted">{{ progressChunks }}</small>
          </div>
        </div>
        <div class="small text-muted mt-3 text-center">
          <div class="fw-bold text-dark">{{ $t('competitions.messages.upload_progress_label') || 'Progreso:' }}</div>
          <div v-if="isCompleted" class="text-success fw-semibold">{{ $t('competitions.messages.upload_complete') || 'Subida completada (100%). Ya puedes cerrar este mensaje.' }}</div>
          <div v-else class="text-secondary">{{ $t('common.uploading') || 'Subiendo...' }}</div>
        </div>
      </div>

      <div v-if="isUploading" class="alert alert-danger py-2 px-3 mb-2" role="alert" style="font-weight:700;">
        {{ $t('competitions.messages.upload_do_not_close') || 'NO CIERRES NI RECARGUES EL NAVEGADOR HASTA QUE LA SUBIDA TERMINE (100%).' }}
      </div>

      <p v-if="meta" class="small text-muted mb-1">
        {{ $t('competitions.messages.video_meta') || 'Detectado:' }} {{ meta.width }}x{{ meta.height }} ({{ meta.sizeMb }} MB)
      </p>
      <p v-if="issues && issues.length" class="text-warning small mb-1">
        {{ error || ($t('competitions.messages.video_warning') || 'Revisa la orientación, resolución o tamaño del video antes de subir.') }}
      </p>
      <p v-if="error && (!issues || !issues.length)" class="text-danger small">{{ error }}</p>

      <div class="d-flex gap-2 justify-content-end mt-2">
        <button
          class="btn btn-sm btn-secondary"
          :disabled="isUploading"
          @click="$emit('close')"
        >
          {{ isCompleted ? ($t('common.close') || 'Cerrar') : ($t('common.cancel') || 'Cancelar') }}
        </button>
        <button
          v-if="!isCompleted"
          class="btn btn-sm btn-primary"
          :disabled="disableConfirm || isUploading"
          @click="$emit('confirm')"
        >
          {{ isUploading ? ($t('common.uploading') || 'Subiendo...') : ($t('competitions.upload_manual_badge') || 'Subir') }}
        </button>
        <button
          v-if="canRetry"
          class="btn btn-sm btn-outline-primary"
          :disabled="isUploading"
          @click="$emit('retry')"
        >
          {{ $t('common.retry') || 'Reintentar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UploadModal',
  props: {
    visible: { type: Boolean, default: false },
    athlete: { type: Object, default: null },
    roundLabel: { type: String, default: '' },
    code: { type: String, default: '' },
    meta: { type: Object, default: null },
    issues: { type: Array, default: () => [] },
    error: { type: String, default: '' },
    progressPercent: { type: Number, default: null },
    progressChunks: { type: String, default: '' },
    isUploading: { type: Boolean, default: false },
    isCompleted: { type: Boolean, default: false },
    canRetry: { type: Boolean, default: false },
    disableConfirm: { type: Boolean, default: false },
  },
  data() {
    return {
      displayPercent: 0,
      percentAnimFrame: null,
    };
  },
  computed: {
    progressMedallionStyle() {
      const pct = Math.max(0, Math.min(100, this.progressPercent || 0));
      const color = this.isCompleted ? '#22c55e' : '#0ea5e9';
      const sweep = pct * 3.6;
      // Layer 1: active ticks via conic-gradient clipped by mask; Layer 2: base ticks via repeating-conic-gradient
      return {
        backgroundImage: `conic-gradient(${color} ${sweep}deg, transparent ${sweep}deg), repeating-conic-gradient(#e5e7eb 0deg 6deg, transparent 6deg 9deg)`,
        maskImage: 'repeating-conic-gradient(#000 0deg 6deg, transparent 6deg 9deg)',
        WebkitMaskImage: 'repeating-conic-gradient(#000 0deg 6deg, transparent 6deg 9deg)'
      };
    },
  },
  watch: {
    progressPercent: {
      immediate: true,
      handler(val) {
        const target = Number.isFinite(val) ? Math.max(0, Math.min(100, val)) : 0;
        this.animatePercent(target);
      }
    }
  },
  methods: {
    animatePercent(target) {
      if (this.percentAnimFrame) cancelAnimationFrame(this.percentAnimFrame);
      const start = this.displayPercent;
      const duration = 300;
      const startTime = performance.now();
      const step = (ts) => {
        const t = Math.min(1, (ts - startTime) / duration);
        this.displayPercent = Math.round(start + (target - start) * t);
        if (t < 1) {
          this.percentAnimFrame = requestAnimationFrame(step);
        }
      };
      this.percentAnimFrame = requestAnimationFrame(step);
    },
    onFileChange(e) {
      const file = e?.target?.files?.[0] || null;
      this.$emit('file-change', file);
    },
  },
  beforeUnmount() {
    if (this.percentAnimFrame) cancelAnimationFrame(this.percentAnimFrame);
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  padding: 12px;
}

.modal-card {
  background: white;
  padding: 16px;
  border-radius: 8px;
  width: 460px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.code-reminder {
  font-size: 0.9rem;
}

.code-pill {
  display: inline-block;
  margin-left: 6px;
  padding: 4px 8px;
  background: #eef2ff;
  color: #3730a3;
  border-radius: 6px;
  font-family: monospace;
  border: 1px solid #c7d2fe;
}

.progress-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 12px 0 16px;
}

.progress-medallion {
  position: relative;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  background-repeat: no-repeat;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12), inset 0 0 0 10px #f1f5f9;
  transition: box-shadow 0.3s ease;
}

.progress-medallion::after {
  content: '';
  position: absolute;
  inset: 34px;
  background: radial-gradient(circle, #ffffff 0%, #f8fafc 70%);
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.05);
  z-index: 1;
}

.progress-medallion__value {
  position: relative;
  text-align: center;
  line-height: 1.25;
  z-index: 2;
}

.progress-medallion--done {
  box-shadow: 0 12px 32px rgba(34, 197, 94, 0.28), inset 0 0 0 10px rgba(34, 197, 94, 0.12);
}
</style>
